/**
 * User Sync Helper
 *
 * Returns the signed-in person's row id in the users table, adding the row the
 * first time if nothing on the server has. The usual first row comes from the
 * server (accept-invite, create-user, scim); this covers a login that has none.
 */

import { getSupabaseClient } from './supabaseClient';

/**
 * Gets or creates the signed-in person's users row.
 * Returns the UUID to use for database foreign keys.
 *
 * The row is the person's own: the database accepts a browser insert only when
 * its id and external_id are their login and its email is their login email
 * (migration 1016), so those come from the session.
 */
export async function getOrCreateSupabaseUser(clerkUserId: string): Promise<string> {
  const supabase = getSupabaseClient();
  
  // First, try to find existing user by external_id
  const { data: existingUser, error: findError } = await supabase
    .from('users')
    .select('id')
    .eq('external_id', clerkUserId)
    .single();
  
  if (existingUser && !findError) {
    return existingUser.id;
  }
  
  // User doesn't exist — try to insert. Use insert (not upsert) so we never
  // overwrite an existing user's name with the placeholder fallback.
  const { data: { user: login } } = await supabase.auth.getUser();
  const metadataName = login?.user_metadata?.full_name;
  const userName = typeof metadataName === 'string' && metadataName.trim()
    ? metadataName.trim()
    : `User ${clerkUserId.slice(0, 8)}`;

  const { data: newUser, error: createError } = await supabase
    .from('users')
    .insert({ id: clerkUserId, external_id: clerkUserId, email: login?.email ?? '', name: userName })
    .select('id')
    .single();
  
  if (!createError && newUser) {
    return newUser.id;
  }

  // unique_violation (23505) means a concurrent request already created this user,
  // or their row carries a different external_id. Re-fetch it rather than
  // overwriting their data.
  if (createError?.code === '23505') {
    const { data: racedUser, error: raceError } = await supabase
      .from('users')
      .select('id')
      .or(`id.eq.${clerkUserId},external_id.eq.${clerkUserId}`)
      .limit(1)
      .single();
    if (racedUser && !raceError) return racedUser.id;
  }

  console.error('Failed to create user record:', createError);
  console.error('Error details:', JSON.stringify(createError, null, 2));
  throw new Error(`Failed to create user record: ${createError?.message}. Check console for details. Make sure RLS policies allow INSERT on users table.`);
}
